import type { FunctionComponent, ReactNode } from 'react'

import {
	BodyS,
	BodySStrong,
	COLORS,
	FlowPage,
	H1,
	IconSquare,
	SPACING,
} from '@monthio/design-system'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { ExtraOrdinaryItemGroupType } from '../SavingsCapacity.types'

import { categoryToIconMap } from './ExtraordinaryItemsGroupSelectorPage'
import { SearchBar } from './SearchBar'

const SubheaderAndIconContainer = styled.div`
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: ${SPACING.s};
`

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.s};
`

const AdaptedContent = styled(FlowPage.Content).attrs(() => ({
	$spacing: 'xs',
}))`
	padding-block-start: 0;
`

const StickyContent = styled.div`
	position: sticky;
	top: 0px;
	z-index: 2;
	background-color: #f8f8f8;
	padding: ${SPACING.l} 0px ${SPACING.m} 0px;
`

const SearchBarContainer = styled.div`
	padding-top: ${SPACING.m};
`

interface ExtraordinaryItemsTransactionsBasePageProps {
	children: ReactNode
	buttons: ReactNode[]
	onGoBackClick: () => void
	category: ExtraOrdinaryItemGroupType
	title?: string
	subtitle?: string
	searchValue?: string
	onSearchChange?: React.Dispatch<React.SetStateAction<string | null>>
}

export const ExtraordinaryItemsGroupBasePage: FunctionComponent<
	ExtraordinaryItemsTransactionsBasePageProps
> = ({
	children,
	buttons,
	onGoBackClick,
	category,
	title,
	subtitle,
	searchValue,
	onSearchChange,
}) => {
	const { t } = useTranslation()

	return (
		<FlowPage>
			<AdaptedContent>
				<StickyContent>
					<HeaderContainer>
						<SubheaderAndIconContainer>
							<IconSquare
								icon={categoryToIconMap[category]}
								size="m"
								backgroundColor={COLORS.graphics.surface.nonSemantic.brightBlue}
								color={COLORS.graphics.surface.nonSemantic.brightBlueInverted}
							/>
							<H1>
								{t(
									`savingsCapacity-extraordinaryItemsCategories-category-${category}-title`,
								)}
							</H1>
						</SubheaderAndIconContainer>
						{title !== undefined && <BodySStrong>{title}</BodySStrong>}
						{subtitle !== undefined && <BodyS>{subtitle}</BodyS>}
					</HeaderContainer>

					{searchValue !== undefined && onSearchChange && (
						<SearchBarContainer>
							<SearchBar value={searchValue} onChange={onSearchChange} />
						</SearchBarContainer>
					)}
				</StickyContent>
				{children}
			</AdaptedContent>
			<FlowPage.Footer
				actions={buttons}
				backButton={
					<FlowPage.BackButton
						onClick={onGoBackClick}
						ariaLabel={t('btn-back')}
					/>
				}
			/>
		</FlowPage>
	)
}
